import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getDisplayText, formatMonthToInput, formatDayToInput } from './functions';
import { useDateInput } from './hooks/use-date-input';
import { DatePickerContextValue, DatePickerBaseProps } from './types';

const DatePickerContext = createContext<DatePickerContextValue | undefined>(undefined);

export const useDatePickerContext = () => {
  const context = useContext(DatePickerContext);
  if (!context) {
    throw new Error('DatePicker compound components must be used within DatePicker');
  }
  return context;
};

interface DatePickerProviderProps extends DatePickerBaseProps {
  children: React.ReactNode;
  enableManualInput?: boolean;
}

export const DatePickerProvider: React.FC<DatePickerProviderProps> = ({
  value,
  onChange,
  placeholder = 'Selecione a data',
  disabled = false,
  variant = 'day',
  enableManualInput = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value || new Date());

  useEffect(() => {
    if (value) setCurrentMonth(value);
  }, [value]);

  const dateInput = useDateInput({
    value,
    onChange,
    onOpenChange: setIsOpen,
    enabled: enableManualInput,
    variant,
  });

  const handleDateClick = (date: Date) => {
    onChange(date);
    setIsOpen(false);
    dateInput.setIsInputMode(false);
  };

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dateInput.setIsInputMode(false);
    setIsOpen(!isOpen);
  };

  const handleEnterInputMode = () => {
    if (!enableManualInput) return;
    const serialized = variant === 'month' ? formatMonthToInput(value) : formatDayToInput(value);
    dateInput.setIsInputMode(true);
    dateInput.setInputValue(serialized);
  };

  const displayText = useMemo(() => getDisplayText(value, variant, placeholder), [value, variant, placeholder]);

  const contextValue: DatePickerContextValue = {
    value,
    onChange,
    placeholder,
    disabled,
    variant,
    isOpen,
    setIsOpen,
    currentMonth,
    setCurrentMonth,
    displayText,
    inputValue: dateInput.inputValue,
    setInputValue: dateInput.setInputValue,
    isInputMode: dateInput.isInputMode,
    setIsInputMode: (mode) => {
      if (mode) {
        handleEnterInputMode();
      } else {
        dateInput.setIsInputMode(false);
      }
    },
    inputRef: dateInput.inputRef,
    handleDateClick,
    handleInputChange: dateInput.handleInputChange,
    handleInputBlur: dateInput.handleInputBlur,
    handleInputKeyDown: dateInput.handleInputKeyDown,
    handleCalendarIconClick,
  };

  return (
    <DatePickerContext.Provider value={contextValue}>
      <div className="relative">{children}</div>
    </DatePickerContext.Provider>
  );
};
